import { useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { COLORS } from '../../../utils/Application_Constants';
import StatusPill from '../Invoicing/StatusPill';
import { invoiceStatusEnum } from '../../Configurations/invoicingConfig';
import { openInvoicePdf } from '../../Services/InvoiceService';
import useGetInvoiceDocuments from '../../hooks/useGetInvoiceDocuments';
import { useToast } from '../../Contexts/ToastContext';

const getDisplayStatus = ({ status, isPreInvoice }) => {
    if (isPreInvoice && status === invoiceStatusEnum.Invoiced) {
        return invoiceStatusEnum.PreInvoiced;
    }
    return status;
};

const InvoiceDocumentLink = ({ document, onOpenPdf }) => {
    const [isLoading, setIsLoading] = useState(false);
    const displayStatus = getDisplayStatus(document);
    const documentType =
        document.documentType || (document.isPreInvoice ? 'Pre-Invoice' : 'Invoice');

    const handleClick = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            await onOpenPdf(document.id);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={isLoading}
            aria-label={`Open ${documentType} ${document.invoiceNumber} PDF`}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                color: 'inherit',
                cursor: isLoading ? 'wait' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
                border: 'none',
                background: 'none',
                padding: 0,
            }}
        >
            <Stack gap={0.75}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography
                        fontSize={14}
                        fontWeight={600}
                        color={COLORS.LT_JOURNEY}
                        lineHeight="20px"
                        sx={{ textDecoration: 'underline' }}
                    >
                        {documentType} {document.invoiceNumber || '—'}
                    </Typography>

                    {displayStatus ? (
                        <StatusPill
                            status={displayStatus}
                            sx={{ minWidth: 'auto', height: 22, fontSize: 11 }}
                        />
                    ) : null}
                </Stack>
            </Stack>
        </button>
    );
};

const AssociatedInvoiceDocuments = ({ processedTimesheetId }) => {
    const { showErrorToast } = useToast();
    const {
        data: documents = [],
        isLoading,
        isError,
    } = useGetInvoiceDocuments({ processedTimesheetId });

    const handleOpenPdf = async (invoiceId) => {
        try {
            await openInvoicePdf(invoiceId);
        } catch (error) {
            console.error('Error opening invoice PDF:', error);
            showErrorToast('Unable to open the invoice PDF. Please try again.');
        }
    };

    return (
        <Stack gap={2}>
            <Typography fontWeight={600} fontSize={16} color={COLORS.LT_JOURNEY175}>
                Associated Invoices
                {!isLoading && !isError ? ` (${documents.length})` : ''}
            </Typography>

            {isLoading && <CircularProgress size={24} />}

            {!isLoading && isError && (
                <Typography fontSize={14} fontWeight={400} color={COLORS.LT_MIDNIGHT50}>
                    Unable to load associated invoices
                </Typography>
            )}

            {!isLoading && !isError && documents.length === 0 && (
                <Typography fontSize={14} fontWeight={400} color={COLORS.LT_MIDNIGHT50}>
                    No associated invoices or pre-invoices
                </Typography>
            )}

            {!isLoading && !isError && documents.length > 0 && (
                <Stack gap={2}>
                    {documents.map((document) => (
                        <InvoiceDocumentLink
                            key={document.id}
                            document={document}
                            onOpenPdf={handleOpenPdf}
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

export default AssociatedInvoiceDocuments;
